import type { Locale } from "./i18n";
import { CONTACT_EMAIL } from "./contact";

/**
 * Long-form legal documents (Terms, Privacy, Accessibility statement), he/en.
 *
 * !! Fill these in before going live — they are referenced throughout the documents:
 */
export const LEGAL_ENTITY = {
  brand: "ChatiSafe",
  brandHe: "צ'אטיסייף",
  /** Registered business / company name + number (ח.פ. / ע.מ.) */
  legalName: "ChatiSafe [שם העסק הרשום]",
  regNumber: "[ח.פ. / ע.מ.]",
  address: "[כתובת רשומה], ישראל",
  email: CONTACT_EMAIL,
  /** Accessibility coordinator (רכז/ת נגישות) — name + phone are required by the regulations */
  a11yCoordinator: "[שם רכז/ת הנגישות]",
  a11yPhone: "[טלפון]",
  privacyOfficer: "[שם ממונה הגנת הפרטיות]",
  updated: "2026-09-17",
  price: 15,
  periodDays: 30,
  trialDays: 3,
  /**
   * Receipt document type (הוראות ניהול פנקסים, סעיף 5 / תקנה 9א למע"מ):
   *  - "receipt"             → קבלה only (עוסק פטור / not VAT-registered). Price shown as-is.
   *  - "tax_invoice_receipt" → חשבונית מס/קבלה with net + VAT lines (עוסק מורשה).
   * Confirm with your accountant before going live.
   */
  receiptDocType: "receipt" as "receipt" | "tax_invoice_receipt",
  vatRate: 0.18,
};

export type LegalBlock = string | { list: string[] } | { ol: string[] } | { note: string };
export type LegalSection = { id: string; heading: string; blocks: LegalBlock[] };
export type LegalDoc = { title: string; intro: string; sections: LegalSection[] };

const E = LEGAL_ENTITY;

/* ====================================================================================== */
/*                                        TERMS                                           */
/* ====================================================================================== */

const termsHe: LegalDoc = {
  title: "תנאי שימוש",
  intro:
    `מסמך זה מסדיר את השימוש בשירות ${E.brandHe} (להלן: "השירות"), המופעל על ידי ${E.legalName}, ${E.regNumber}, ${E.address} (להלן: "המפעילה", "אנחנו"). ` +
    `השימוש בשירות, לרבות הרשמה, הוספת ילד, קישור חשבון וואטסאפ או ביצוע תשלום, מהווה הסכמה מלאה ובלתי מסויגת לתנאים אלה ולמדיניות הפרטיות המהווה חלק בלתי נפרד מהם. אם אינך מסכים/ה לתנאים — אל תשתמש/י בשירות. ` +
    `התנאים מנוסחים בלשון זכר מטעמי נוחות בלבד ומופנים לכל המינים.`,
  sections: [
    {
      id: "service",
      heading: "1. מהות השירות",
      blocks: [
        "השירות הוא כלי עזר טכנולוגי להורים, המנתח באופן אוטומטי הודעות ואירועים בחשבון וואטסאפ של ילד קטין שקושר לשירות על ידי ההורה, במטרה לאתר סימנים אפשריים לבריונות, איומים, הטרדה, תוכן לא הולם או מגע עם גורמים לא מוכרים, ולהתריע על כך להורים.",
        "השירות מבוסס, בין היתר, על מודלים של בינה מלאכותית וכללים אוטומטיים. מטבעם, כלים אלה עלולים לפספס תוכן מסוכן (false negative) או להתריע על תוכן תמים (false positive). השירות אינו מבטיח זיהוי של כל סיכון, אינו מנטר בזמן אמת באופן רציף ואינו מהווה תחליף להשגחה הורית, לשיחה עם הילד או לפנייה לגורמי מקצוע.",
        "השירות אינו שירות חירום. במקרה של סכנה מיידית לילד יש לפנות למשטרת ישראל (100), למוקד 105 להגנה על ילדים ברשת, או לגורמי חירום מתאימים במדינתך.",
        `השירות אינו קשור, אינו ממומן ואינו מאושר על ידי WhatsApp LLC או Meta Platforms, Inc. "WhatsApp" הוא סימן מסחר של בעליו. קישור השירות לחשבון וואטסאפ נעשה באמצעות מנגנון "מכשירים מקושרים" ועלול לעמוד בסתירה לתנאי השימוש של וואטסאפ; הסיכון לכך, לרבות הגבלה או חסימה של חשבון הוואטסאפ של הילד, חל על המשתמש בלבד.`,
      ],
    },
    {
      id: "eligibility",
      heading: "2. כשירות והצהרות המשתמש",
      blocks: [
        "השימוש בשירות מותר אך ורק לבגירים (מעל גיל 18) שהם הורה או אפוטרופוס חוקי של הקטין שאת חשבונו הם מבקשים לקשר. בעצם השימוש אתה מצהיר ומתחייב כי:",
        {
          list: [
            "אתה ההורה או האפוטרופוס החוקי של כל ילד שתוסיף לשירות, והילד קטין (מתחת לגיל 18) המתגורר עמך או נתון לאחריותך ההורית;",
            "אתה מוסמך על פי דין להסכים בשם הקטין לעיבוד המידע המתואר במדיניות הפרטיות, ואתה נושא באחריות הבלעדית להחלטה לנטר את חשבונו;",
            "תפעל בהתאם לחובותיך כהורה ולדין החל, לרבות חוק הכשרות המשפטית והאפוטרופסות, התשכ\"ב-1962, חוק הגנת הפרטיות, התשמ\"א-1981, חוק האזנת סתר, התשל\"ט-1979 וחוק המחשבים, התשנ\"ה-1995, ותשקול ליידע את הילד על קיום הניטור באופן המתאים לגילו ולבגרותו;",
            "לא תשתמש בשירות לניטור של אדם בגיר, של קטין שאינך אפוטרופוסו, של בן/בת זוג, של עובד או של כל אדם אחר. שימוש כזה אסור בהחלט, עלול להוות עבירה פלילית ועוולה אזרחית, ויביא לסגירה מיידית של החשבון;",
            "הפרטים שמסרת בהרשמה נכונים ומעודכנים, ומספרי הטלפון של ההורים שהוגדרו לקבלת דיווחים שייכים לך או לאדם שנתן לך הסכמה מפורשת לקבל הודעות שירות מאיתנו;",
            "לא תעביר לצד שלישי דיווחים או תכנים שהתקבלו מהשירות, אלא לצורך הגנה על הילד (למשל, לגורמי חינוך, רווחה או אכיפת חוק).",
          ],
        },
        "המפעילה אינה מסוגלת לאמת את מעמדך כהורה או אפוטרופוס, ומסתמכת על הצהרתך בלבד. אתה תישא באחריות הבלעדית והמלאה לכל תוצאה של הצהרה שאינה נכונה.",
      ],
    },
    {
      id: "account",
      heading: "3. חשבון, סיסמה ואבטחה",
      blocks: [
        "ההרשמה מחייבת כתובת דוא\"ל מאומתת וסיסמה. אתה אחראי לשמירת סודיות פרטי הגישה ולכל פעולה שתתבצע בחשבונך. יש להודיע לנו מיד על כל שימוש בלתי מורשה.",
        "אנו רשאים להשעות או לסגור חשבון, באופן זמני או קבוע, בכל מקרה של הפרת תנאים אלה, חשד לשימוש בלתי חוקי, פגיעה בצדדים שלישיים, או דרישה של רשות מוסמכת.",
      ],
    },
    {
      id: "payments",
      heading: "4. תשלומים, תקופת ניסיון, חידוש ומדיניות ללא החזרים",
      blocks: [
        `מחיר השירות הוא ${E.price} ₪ לכל ילד לתקופה של ${E.periodDays} ימים (להלן: "תקופת חיוב"), כולל מע\"מ כדין. תקופת החיוב היא תמיד ${E.periodDays} ימים קלנדריים רצופים מרגע אישור התשלום, ללא קשר למספר הימים בחודש הקלנדרי. התשלום הוא תשלום מראש בעד תקופת החיוב כולה.`,
        `הילד הראשון בלבד שנוסף לחשבון זכאי לתקופת ניסיון חינם של ${E.trialDays} ימים, ללא צורך בפרטי אשראי או באמצעי תשלום. בתום תקופת הניסיון ההגנה על אותו ילד מושהית אוטומטית עד לביצוע תשלום. תקופת הניסיון נרשמת לפי כתובת הדוא"ל של החשבון ולפי מספרי הטלפון של ההורים כפי שהיו בעת תחילת הניסיון, וניתנת פעם אחת בלבד לכל אחד מהם. פתיחת חשבון חדש עם אותו דוא"ל או עם מספר טלפון של הורה שכבר שימש לניסיון אינה מזכה בניסיון נוסף. הניסיון אינו ניתן להעברה. כל ילד נוסף מחויב מיד עם הוספתו.`,
        "תחילת השירות מיידית: בעת אישור התשלום אתה מבקש במפורש שההגנה על הילד תתחיל מיידית, עוד בתוך תקופת הביטול הקבועה בחוק, ומאשר כי ידוע לך שהשירות הוא שירות מתמשך שמתן שלו מתחיל ברגע התשלום. אישור זה נרשם אצלנו יחד עם פרטי העסקה.",
        {
          note:
            "מדיניות ללא החזרים: כל תקופת חיוב משולמת מראש ואינה ניתנת להחזר, במלואה או בחלקה. אי־שימוש בשירות, ניתוק חשבון הוואטסאפ של הילד, הפסקת הצורך בניטור, בקשה להסרת הילד או סגירת החשבון — אינם מזכים בהחזר, בזיכוי או בהעברת יתרת התקופה לילד אחר. האמור כפוף אך ורק לזכויות הביטול שאינן ניתנות להתניה על פי חוק, המפורטות להלן.",
        },
        `הסרת ילד במהלך תקופה משולמת: מאחר שהתקופה משולמת מראש ואינה מוחזרת, לא ניתן למחוק מהחשבון ילד שעבורו תקופת חיוב משולמת עדיין בתוקף, וכן ילד שהחידוש האוטומטי עבורו פעיל. כפתור המחיקה יופעל רק בתום תקופת החיוב וכאשר החידוש האוטומטי כבוי. אין בכך כדי לפגוע בזכותך להפסיק את הניטור בכל רגע — באמצעות ניתוק הוואטסאפ או השהיית ההגנה בלוח הבקרה — אך הפסקה כזו אינה מזכה בהחזר. אם ברצונך להגן על ילד אחר במקום הילד שעבורו שולם: לחץ על "ניתוק" בכרטיס הילד, ואז קשר מחדש באמצעות QR את חשבון הוואטסאפ של הילד האחר; תקופת החיוב ממשיכה לחול על אותו כרטיס ילד ללא תשלום נוסף.`,
        "בעת התשלום ניתן לבחור בין תשלום חד־פעמי לבין חידוש אוטומטי. בבחירה בחידוש אוטומטי אתה מאשר לנו לחייב את אמצעי התשלום השמור בסך המחיר הנקוב בתום כל תקופת חיוב, עבור אותו ילד, עד לביטול על ידך. עסקה זו היא \"עסקה מתמשכת\" כמשמעותה בסעיף 13ד לחוק הגנת הצרכן, התשמ\"א-1981. ניתן לכבות את החידוש האוטומטי בכל עת מלוח הבקרה; הכיבוי מונע חיובים עתידיים בלבד ואינו מזכה בהחזר בעד התקופה שכבר שולמה, שתימשך עד תומה. חיוב חידוש שבוצע בעוד החידוש האוטומטי פעיל הוא חיוב תקף בעד תקופה חדשה, ואינו מוחזר.",
        "אם חיוב חידוש נכשל (למשל, כרטיס שפג תוקפו), ההגנה על הילד תושהה בתום התקופה עד להסדרת התשלום. נשלח לך הודעה על כך.",
        `זכות ביטול על פי חוק (החריג היחיד למדיניות ללא החזרים): הוראות חוק הגנת הצרכן ותקנות הגנת הצרכן (ביטול עסקה), התשע\"א-2010, הן הוראות קוגנטיות, ואין בתנאים אלה כדי לגרוע מהן. לפי סעיף 14ג(ג)(2) לחוק, צרכן רשאי לבטל עסקת מכר מרחוק לשירות מתמשך בתוך 14 ימים מיום ביצוע העסקה (או מיום קבלת מסמך פרטי העסקה, לפי המאוחר), גם אם השירות כבר החל. במקרה כזה, ובהתאם לסעיף 14ה לחוק, תשלם את התמורה היחסית בעד הימים שבהם ניתן לך השירות (${E.price} ₪ ÷ ${E.periodDays} = 0.50 ₪ ליום), והמפעילה רשאית לגבות דמי ביטול בשיעור 5% ממחיר העסקה או 100 ₪, לפי הנמוך מביניהם (כלומר 0.75 ₪). היתרה תוחזר לאמצעי התשלום בתוך 14 ימים מקבלת הודעת הביטול. לדוגמה: ביטול ביום העשירי לתקופה יזכה בהחזר של ${E.price} − 5.00 − 0.75 = 9.25 ₪. לאחר 14 ימים מיום העסקה לא קיימת זכות ביטול על פי חוק, ומדיניות ללא החזרים חלה במלואה. אזרח ותיק, אדם עם מוגבלות או עולה חדש זכאים לבטל בתוך ארבעה חודשים מיום העסקה, בכפוף להצגת תעודה מתאימה ולתנאי סעיף 14ג1 לחוק. הזכות חלה על עסקת הרכישה הראשונה בלבד לכל ילד, ולא על חיובי חידוש של אותה עסקה מתמשכת, שלגביהם עומדת לך הזכות להפסיק חיובים עתידיים לפי סעיף 13ד. תקופת ניסיון חינם אינה עסקה בתמורה ואינה מקנה זכות להחזר כלשהו.`,
        `הודעת ביטול על פי חוק תישלח לדוא\"ל ${E.email} בלבד, ותכלול: שם מלא, דוא\"ל החשבון, שם הילד ומזהה העסקה (מופיע בהיסטוריית התשלומים בלוח הבקרה). מועד קבלת ההודעה בדוא\"ל הוא המועד הקובע. לא תתקבל הודעת ביטול בדרך אחרת. ביטול לפי סעיף זה מסיים את תקופת החיוב של אותו ילד מיידית, והילד יימחק מהחשבון.`,
        "משתמשים מחוץ לישראל: אם הדין במקום מושבך מקנה לך זכות ביטול שאינה ניתנת להתניה (למשל, זכות החזרה בת 14 ימים לפי דירקטיבת זכויות הצרכן 2011/83/EU), היא תחול בהתאם לאותו דין. בכפוף לדין כאמור, בקשתך המפורשת לתחילת שירות מיידית ואישורך בעת התשלום מהווים הסכמה לכך שתחויב בתמורה יחסית בעד השירות שניתן עד לביטול, וכי עם השלמת תקופת החיוב זכות הביטול פוקעת.",
        "אנו רשאים לעדכן את המחיר מעת לעת. שינוי מחיר יחול רק על תקופות חיוב חדשות, ותימסר על כך הודעה מוקדמת של 30 ימים לפחות בדוא\"ל או בלוח הבקרה. המשך שימוש לאחר כניסת השינוי לתוקף מהווה הסכמה.",
        "התשלומים מתבצעים באמצעות ספק סליקה חיצוני. אנו לא שומרים את מספר הכרטיס המלא, אלא ארבע ספרות אחרונות וסוג הכרטיס בלבד לצורכי זיהוי. הגשת הכחשת עסקה (chargeback) בניגוד לתנאים אלה, בעוד השירות ניתן כסדרו, מהווה הפרה של התנאים ותקנה למפעילה זכות להשעות את החשבון ולנקוט בהליכים לגביית החוב.",
        "קבלות: בעד כל תשלום (לרבות חיוב חידוש אוטומטי) תופק קבלה ממוספרת ותישלח כ\"מסמך ממוחשב\" — בהתאם לסעיף 18ב להוראות מס הכנסה (ניהול פנקסי חשבונות), התשל\"ג-1973 — לכתובת הדוא\"ל של החשבון וכן לכתובת נוספת שתבחר בעת התשלום (אם בחרת). אישור התשלום מהווה הסכמתך לקבלת מסמכים ממוחשבים בדוא\"ל; ניתן לבטל הסכמה זו בפנייה אלינו ולקבל קבלה בדרך אחרת. עותק של כל קבלה זמין להורדה בהיסטוריית התשלומים בלוח הבקרה. הודעות שירות בוואטסאפ להורים המוגדרים בחשבון יישלחו גם בעת הפעלת הגנה, לפני חידוש אוטומטי (כ־3 ימים ויום אחד לפני החיוב), ובעת השהיה, ניתוק, שינוי שם או מחיקה של ילד.",
        {
          note: "בגרסת הפיתוח הנוכחית שער התשלום הוא הדגמה בלבד ואינו מבצע חיוב אמיתי.",
        },
      ],
    },
    {
      id: "suspension",
      heading: "5. השהיית הגנה בהיעדר תשלום",
      blocks: [
        "בתום תקופת חיוב או ניסיון שלא חודשה, השירות מפסיק לסרוק את ההודעות של אותו ילד ולא נשלחות התראות להורים. ההורים המוגדרים בחשבון יקבלו הודעה אחת על ההשהיה ועל הצורך בתשלום. אנו לא נישא באחריות לכל אירוע שהתרחש בזמן שההגנה הייתה מושהית, מכל סיבה שהיא.",
      ],
    },
    {
      id: "acceptable-use",
      heading: "6. שימוש אסור",
      blocks: [
        "חל איסור מוחלט:",
        {
          list: [
            "לנטר אדם שאינך אפוטרופוסו החוקי או שאינו קטין;",
            "לעשות שימוש בשירות לצורך מעקב, הטרדה, סחיטה, איסוף מידע לשם פגיעה בצד שלישי, או לכל מטרה בלתי חוקית;",
            "לפרסם, להפיץ או להעביר תוכן שנאסף באמצעות השירות שלא לצורך הגנה על הילד;",
            "לנסות לעקוף מנגנוני אבטחה או תשלום, לבצע הנדסה לאחור, לסרוק, להעמיס או לשבש את השירות;",
            "להשתמש בשירות באופן המפר זכויות של צדדים שלישיים, לרבות זכויות פרטיות של אנשי הקשר של הילד.",
          ],
        },
      ],
    },
    {
      id: "third-parties",
      heading: "7. תלות בצדדים שלישיים וזמינות",
      blocks: [
        "השירות תלוי בתשתיות ובשירותים של צדדים שלישיים — לרבות וואטסאפ, ספקי ענן, ספקי מודלים של בינה מלאכותית וספקי סליקה — שאינם בשליטתנו. שינוי, תקלה או הפסקה בשירותיהם עלולים לפגוע בזמינות השירות או בתפקודו, ולא תהיה לנו אחריות בגינם.",
        "השירות ניתן כמות שהוא (AS IS) וככל שהוא זמין (AS AVAILABLE). אנו לא מתחייבים לזמינות רציפה, לרציפות סריקה, לזמני תגובה, או לכך שהשירות יהיה נקי משגיאות. נשתדל להודיע מראש על תחזוקה מתוכננת, אך איננו מחויבים לכך.",
      ],
    },
    {
      id: "ip",
      heading: "8. קניין רוחני",
      blocks: [
        "כל הזכויות בשירות — לרבות התוכנה, העיצוב, הלוגו, השם, הטקסטים, האלגוריתמים והתכנים — שייכות למפעילה או לנותני רישיון מטעמה, ומוגנות בדיני קניין רוחני בישראל ובעולם. ניתנת לך זכות שימוש אישית, מוגבלת, בלתי ייחודית, שאינה ניתנת להעברה, לשימוש בשירות לצרכיך המשפחתיים בלבד. המשוב שתעביר לנו יהיה ניתן לשימוש חופשי על ידינו ללא תמורה.",
      ],
    },
    {
      id: "liability",
      heading: "9. הגבלת אחריות",
      blocks: [
        "מבלי לגרוע מהאמור לעיל, ובמידה המרבית המותרת על פי דין:",
        {
          list: [
            "המפעילה, מנהליה, עובדיה וספקיה לא יישאו באחריות לכל נזק ישיר, עקיף, תוצאתי, מיוחד או עונשי, לרבות פגיעה בילד, נזק נפשי, אובדן מידע, אובדן רווח או פגיעה במוניטין, הנובע מהשימוש בשירות או מאי־היכולת להשתמש בו, מאי־זיהוי של תוכן מסוכן, מהתראת שווא, מהשהיית השירות מכל סיבה, או מפעולה של צד שלישי;",
            "האחריות המצטברת הכוללת של המפעילה כלפיך, מכל עילה שהיא, לא תעלה על הסכום ששילמת בפועל לשירות ב־12 החודשים שקדמו לאירוע, או 100 ₪, לפי הגבוה מביניהם;",
            "אתה מכיר בכך שהחלטות הנוגעות לילדך — לרבות תגובה להתראה או הימנעות ממנה — הן שלך בלבד, ואינן באחריות המפעילה.",
          ],
        },
        "הגבלות אלה הן יסוד להתקשרות ולמחיר השירות, ולא היינו מציעים את השירות בלעדיהן. אין בהן כדי לגרוע מזכויות שאינן ניתנות להתניה על פי חוק הגנת הצרכן או כל דין קוגנטי אחר.",
      ],
    },
    {
      id: "indemnity",
      heading: "10. שיפוי",
      blocks: [
        "אתה מתחייב לשפות ולפצות את המפעילה, מנהליה, עובדיה וספקיה, מיד עם דרישה ראשונה, בגין כל תביעה, דרישה, נזק, הפסד, הוצאה או חבות (לרבות שכר טרחת עורכי דין), הנובעים מהפרת תנאים אלה על ידך, מהצהרה לא נכונה, משימוש בשירות בניגוד לדין, או מתביעה של צד שלישי (לרבות הילד, ההורה השני, או אנשי קשר של הילד) בקשר לניטור שביצעת באמצעות השירות.",
      ],
    },
    {
      id: "ai",
      heading: "11. עיבוד אוטומטי ובינה מלאכותית",
      blocks: [
        "ההתראות מופקות באופן אוטומטי ללא בדיקה אנושית. ציוני החומרה, הסיווגים והתקצירים הם הערכה סטטיסטית בלבד ואינם קביעה עובדתית. אין להסתמך עליהם כראיה או כבסיס בלעדי להחלטה משמעותית. במקרה של חשד ממשי לפגיעה בילד — יש לפנות לגורם מקצועי.",
      ],
    },
    {
      id: "termination",
      heading: "12. סיום ההתקשרות",
      blocks: [
        "באפשרותך לנתק חשבונות וואטסאפ ולהשהות הגנה בכל עת מלוח הבקרה, ולסגור את חשבונך בפנייה אלינו. מחיקת ילד אפשרית רק בתום תקופת חיוב משולמת וכאשר החידוש האוטומטי כבוי, כמפורט בסעיף 4. סגירת חשבון או הפסקת שימוש אינן מזכות בהחזר כלשהו עבור תקופות חיוב ששולמו, למעט זכות הביטול על פי חוק המפורטת בסעיף 4. הוראות שמטבען נועדו לשרוד את סיום ההתקשרות (לרבות סעיפים 4, 8–10, 13) ימשיכו לחול.",
      ],
    },
    {
      id: "law",
      heading: "13. דין חל וסמכות שיפוט",
      blocks: [
        "על תנאים אלה יחולו דיני מדינת ישראל בלבד, ללא כללי ברירת הדין שלה. סמכות השיפוט הבלעדית בכל מחלוקת נתונה לבתי המשפט המוסמכים במחוז תל אביב–יפו. לפני פנייה לערכאות, הצדדים ינסו ליישב את המחלוקת בתום לב בפנייה בכתב ותוך 30 ימים.",
        "אם אתה משתמש בשירות מחוץ לישראל — אתה אחראי לוודא שהשימוש חוקי במדינתך. ייתכן שחוקי המדינה שלך מקנים לך זכויות צרכניות נוספות שאינן ניתנות להתניה, ואין בתנאים אלה כדי לגרוע מהן.",
      ],
    },
    {
      id: "changes",
      heading: "14. שינויים בתנאים",
      blocks: [
        "אנו רשאים לעדכן תנאים אלה מעת לעת. שינוי מהותי יפורסם באתר ו/או יישלח בדוא\"ל לפחות 14 ימים לפני כניסתו לתוקף. המשך שימוש בשירות לאחר מועד זה מהווה הסכמה לתנאים המעודכנים. הנוסח המחייב הוא זה המפורסם באתר בכל עת. במקרה של סתירה בין הגרסה העברית לגרסה האנגלית — הגרסה העברית גוברת.",
      ],
    },
    {
      id: "misc",
      heading: "15. שונות ויצירת קשר",
      blocks: [
        "אם ייקבע כי הוראה כלשהי בתנאים אלה אינה תקפה — יתר ההוראות ימשיכו לחול במלואן. אי־אכיפה של זכות אינה מהווה ויתור עליה. אינך רשאי להמחות את זכויותיך לפי תנאים אלה ללא הסכמתנו בכתב.",
        `שאלות, פניות וביטולים: ${E.email}. כתובת למשלוח דואר: ${E.address}.`,
      ],
    },
  ],
};

const termsEn: LegalDoc = {
  title: "Terms of Service",
  intro:
    `These Terms govern your use of the ${E.brand} service (the "Service"), operated by ${E.legalName}, ${E.regNumber}, ${E.address} ("we", "us", the "Operator"). ` +
    `By registering, adding a child, linking a WhatsApp account or making a payment you fully and unconditionally accept these Terms and our Privacy Policy, which forms an integral part of them. If you do not agree, do not use the Service.`,
  sections: [
    {
      id: "service",
      heading: "1. Nature of the Service",
      blocks: [
        "The Service is a technological aid for parents. It automatically analyses messages and events in the WhatsApp account of a minor child that the parent has linked to the Service, in order to detect possible signs of bullying, threats, harassment, inappropriate content or contact with unknown parties, and to alert the parents.",
        "The Service relies, among other things, on artificial-intelligence models and automated rules. By their nature these tools may miss harmful content (false negatives) or flag harmless content (false positives). The Service does not guarantee detection of every risk, is not a continuous real-time monitor, and is not a substitute for parental supervision, conversation with your child or professional help.",
        "The Service is not an emergency service. If a child is in immediate danger contact the police (in Israel: 100), the 105 national hotline for child online protection, or the emergency services in your country.",
        `The Service is not affiliated with, sponsored or endorsed by WhatsApp LLC or Meta Platforms, Inc. "WhatsApp" is a trademark of its owner. Linking uses WhatsApp's "Linked Devices" mechanism and may conflict with WhatsApp's own terms; any resulting risk, including restriction or banning of the child's WhatsApp account, rests solely with you.`,
      ],
    },
    {
      id: "eligibility",
      heading: "2. Eligibility and your representations",
      blocks: [
        "The Service may only be used by adults (18+) who are the parent or legal guardian of the minor whose account they link. By using the Service you represent and warrant that:",
        {
          list: [
            "you are the parent or legal guardian of every child you add, and the child is a minor (under 18) living with you or under your parental responsibility;",
            "you are legally entitled to consent on the minor's behalf to the processing described in the Privacy Policy, and you bear sole responsibility for the decision to monitor their account;",
            "you will act in accordance with your parental duties and applicable law — including Israel's Legal Capacity and Guardianship Law 1962, Protection of Privacy Law 1981, Wiretapping Law 1979 and Computers Law 1995, and any equivalent law where you live — and you will consider informing the child of the monitoring in a manner appropriate to their age and maturity;",
            "you will not use the Service to monitor an adult, a minor who is not your ward, a partner, an employee or any other person. Such use is strictly prohibited, may constitute a criminal offence and civil wrong, and will result in immediate account termination;",
            "the details you provided are accurate, and the parent phone numbers you set up to receive alerts belong to you or to a person who has expressly agreed to receive service messages from us;",
            "you will not pass alerts or content obtained from the Service to third parties, except as needed to protect the child (e.g. school, welfare or law-enforcement authorities).",
          ],
        },
        "We cannot verify your status as a parent or guardian and rely solely on your representation. You bear full and exclusive responsibility for any consequence of an inaccurate representation.",
      ],
    },
    {
      id: "account",
      heading: "3. Account, password and security",
      blocks: [
        "Registration requires a verified email address and a password. You are responsible for keeping your credentials confidential and for all activity in your account. Notify us immediately of any unauthorised use.",
        "We may suspend or terminate an account, temporarily or permanently, upon breach of these Terms, suspected unlawful use, harm to third parties, or a demand by a competent authority.",
      ],
    },
    {
      id: "payments",
      heading: "4. Payments, free trial, renewal and no-refund policy",
      blocks: [
        `The price is ₪${E.price} per child per ${E.periodDays}-day billing period, VAT included where applicable. A billing period is always ${E.periodDays} consecutive calendar days from the moment payment is confirmed, regardless of the number of days in the calendar month. Payment is made in advance for the entire billing period.`,
        `Only the first child added to an account receives a ${E.trialDays}-day free trial. No credit card or other payment method is required. When the trial ends, protection for that child is automatically paused until payment is made. The trial is recorded against the account email and the parent phone numbers as they were when the trial started, and is granted once for each of them. A new account that reuses that email or a parent phone which already started a trial does not receive another trial. The trial is not transferable. Every additional child is charged immediately upon being added.`,
        "Immediate start: when confirming payment you expressly request that protection for the child begin immediately, within any statutory cancellation period, and acknowledge that the Service is a continuing service whose performance starts at the moment of payment. This acknowledgement is recorded together with the transaction details.",
        {
          note:
            "No-refund policy: every billing period is prepaid and non-refundable, in whole or in part. Not using the Service, unlinking the child's WhatsApp account, no longer needing monitoring, asking to remove the child or closing the account does not entitle you to a refund, credit or transfer of the remaining period to another child. This is subject only to the non-waivable statutory cancellation rights described below.",
        },
        `Removing a child during a paid period: because the period is prepaid and non-refundable, a child whose paid billing period is still in force — or for whom auto-renewal is switched on — cannot be deleted from the account. The delete button becomes available only once the billing period has ended and auto-renewal is off. This does not limit your right to stop monitoring at any moment — by unlinking WhatsApp or pausing protection in the dashboard — but stopping does not create a right to a refund. If you wish to protect a different child instead of the one you paid for: press "Unlink" on the child's card, then pair the other child's WhatsApp account again via QR; the billing period continues to apply to that child card at no extra charge.`,
        "At checkout you may choose a one-time payment or auto-renewal. By choosing auto-renewal you authorise us to charge your saved payment method the listed price at the end of each billing period for that child until you cancel. This is a \"continuing transaction\" under section 13D of Israel's Consumer Protection Law 1981. You may turn auto-renewal off at any time from the dashboard; doing so only prevents future charges and does not entitle you to a refund for the period already paid, which runs to its end. A renewal charge made while auto-renewal was on is a valid charge for a new period and is not refundable.",
        "If a renewal charge fails (e.g. expired card), protection for that child is paused at the end of the period until payment is settled. We will notify you.",
        `Statutory right of cancellation (the only exception to the no-refund policy): the Consumer Protection Law and the Consumer Protection (Cancellation of Transaction) Regulations 2010 are mandatory, and nothing in these Terms derogates from them. Under section 14C(c)(2) of the Law a consumer may cancel a distance transaction for a continuing service within 14 days of the transaction (or of receiving the transaction details document, whichever is later), even if the service has already started. In that case, under section 14E, you pay the proportional consideration for the days on which the service was provided (₪${E.price} ÷ ${E.periodDays} = ₪0.50 per day) and we may charge a cancellation fee of 5% of the transaction price or ₪100, whichever is lower (i.e. ₪0.75). The balance is refunded to the payment method within 14 days of receiving the cancellation notice. Example: cancelling on day 10 of the period yields a refund of ₪${E.price} − 5.00 − 0.75 = ₪9.25. After 14 days from the transaction there is no statutory right of cancellation and the no-refund policy applies in full. Senior citizens, persons with disabilities and new immigrants may cancel within four months of the transaction, subject to presenting an appropriate certificate and to section 14C1 of the Law. The right applies to the first purchase transaction for each child only, not to renewal charges of the same continuing transaction, for which your right is to stop future charges under section 13D. A free trial is not a transaction for consideration and does not give rise to any refund.`,
        `Statutory cancellation notices must be sent by email to ${E.email} only and must include: full name, account email, the child's name and the order ID (shown in the payment history in the dashboard). The time the email is received is the operative time. Cancellation notices sent by any other means will not be accepted. Cancellation under this paragraph ends that child's billing period immediately and the child is removed from the account.`,
        "Users outside Israel: if the law of your place of residence grants you a non-waivable right of cancellation (for example the 14-day right of withdrawal under Consumer Rights Directive 2011/83/EU), it applies in accordance with that law. Subject to such law, your express request for immediate start and your acknowledgement at checkout constitute consent to be charged a proportional amount for the service provided until withdrawal, and to the right of withdrawal lapsing once the billing period has been fully performed.",
        "We may change the price from time to time. A change applies only to new billing periods and will be announced at least 30 days in advance by email or in the dashboard. Continued use after the effective date constitutes acceptance.",
        "Payments are processed by a third-party payment provider. We do not store your full card number — only the last four digits and card brand for identification. Filing a chargeback contrary to these Terms while the Service is being duly provided is a breach of these Terms and entitles us to suspend the account and pursue recovery of the debt.",
        "Receipts: for every payment (including auto-renewal charges) a numbered receipt is issued and sent as a computerised document — under section 18B of the Israeli Income Tax (Bookkeeping) Regulations 1973 — to the account email and to any additional address you choose at checkout. Confirming payment constitutes your consent to receive computerised documents by email; you may withdraw it by contacting us and receive receipts another way. A copy of every receipt can be downloaded from the payment history in the dashboard. WhatsApp service messages are also sent to the parents configured in the account when protection is activated, before an auto-renewal (about 3 days and 1 day before the charge), and when a child is paused, unlinked, renamed or deleted.",
        { note: "In the current development build the payment gateway is a demo and performs no real charge." },
      ],
    },
    {
      id: "suspension",
      heading: "5. Suspension for non-payment",
      blocks: [
        "When a billing or trial period ends without renewal, the Service stops scanning that child's messages and sends no alerts to parents. The parents configured in the account receive a single notice about the pause and the need to pay. We accept no liability for any event that occurs while protection is paused, for whatever reason.",
      ],
    },
    {
      id: "acceptable-use",
      heading: "6. Prohibited use",
      blocks: [
        "You must not:",
        {
          list: [
            "monitor any person who is not a minor under your legal guardianship;",
            "use the Service for stalking, harassment, extortion, gathering information to harm a third party, or any unlawful purpose;",
            "publish, distribute or transfer content collected through the Service other than to protect the child;",
            "circumvent security or payment mechanisms, reverse-engineer, scrape, overload or disrupt the Service;",
            "use the Service in a way that infringes the rights of third parties, including the privacy of the child's contacts.",
          ],
        },
      ],
    },
    {
      id: "third-parties",
      heading: "7. Third-party dependencies and availability",
      blocks: [
        "The Service depends on infrastructure and services of third parties — including WhatsApp, cloud providers, AI-model providers and payment processors — outside our control. Changes, failures or discontinuation on their side may impair the Service's availability or operation, for which we are not liable.",
        "The Service is provided AS IS and AS AVAILABLE. We do not warrant uninterrupted availability, continuous scanning, response times, or error-free operation. We will try to give notice of planned maintenance but are not obliged to.",
      ],
    },
    {
      id: "ip",
      heading: "8. Intellectual property",
      blocks: [
        "All rights in the Service — software, design, logo, name, texts, algorithms and content — belong to the Operator or its licensors and are protected by intellectual-property law in Israel and worldwide. You receive a personal, limited, non-exclusive, non-transferable right to use the Service for your family's needs only. Feedback you provide may be used by us freely without compensation.",
      ],
    },
    {
      id: "liability",
      heading: "9. Limitation of liability",
      blocks: [
        "Without derogating from the above, and to the maximum extent permitted by law:",
        {
          list: [
            "the Operator, its officers, employees and suppliers shall not be liable for any direct, indirect, consequential, special or punitive damage — including harm to a child, emotional distress, data loss, loss of profit or reputational damage — arising from use of or inability to use the Service, from failure to detect harmful content, from a false alert, from suspension of the Service for any reason, or from the acts of any third party;",
            `the Operator's total aggregate liability to you, on any cause of action, shall not exceed the amount you actually paid for the Service in the 12 months preceding the event, or ₪100, whichever is higher;`,
            "you acknowledge that decisions concerning your child — including whether and how to respond to an alert — are yours alone and not the Operator's responsibility.",
          ],
        },
        "These limitations are fundamental to the bargain and the price of the Service; we would not offer the Service without them. They do not derogate from rights that cannot be waived under the Consumer Protection Law or other mandatory law.",
      ],
    },
    {
      id: "indemnity",
      heading: "10. Indemnity",
      blocks: [
        "You agree to indemnify and hold harmless the Operator, its officers, employees and suppliers, upon first demand, against any claim, demand, damage, loss, expense or liability (including legal fees) arising from your breach of these Terms, an inaccurate representation, unlawful use of the Service, or a third-party claim (including by the child, the other parent, or the child's contacts) in connection with monitoring you carried out through the Service.",
      ],
    },
    {
      id: "ai",
      heading: "11. Automated processing and AI",
      blocks: [
        "Alerts are generated automatically without human review. Severity scores, classifications and excerpts are statistical estimates, not findings of fact. Do not rely on them as evidence or as the sole basis for a significant decision. If you genuinely suspect harm to a child, contact a professional.",
      ],
    },
    {
      id: "termination",
      heading: "12. Termination",
      blocks: [
        "You may unlink WhatsApp accounts and pause protection at any time from the dashboard, and close your account by contacting us. A child can be deleted only after its paid billing period has ended and auto-renewal is off, as set out in section 4. Closing the account or ceasing to use the Service does not entitle you to any refund for paid billing periods, except under the statutory right of cancellation described in section 4. Provisions that by their nature should survive termination (including sections 4, 8–10 and 13) continue to apply.",
      ],
    },
    {
      id: "law",
      heading: "13. Governing law and jurisdiction",
      blocks: [
        "These Terms are governed exclusively by the laws of the State of Israel, without regard to conflict-of-law rules. The competent courts of the Tel Aviv–Jaffa district have exclusive jurisdiction over any dispute. Before commencing proceedings the parties will attempt in good faith to resolve the dispute by written notice within 30 days.",
        "If you use the Service outside Israel you are responsible for ensuring that such use is lawful where you live. Your local law may grant you additional non-waivable consumer rights, which these Terms do not limit.",
      ],
    },
    {
      id: "changes",
      heading: "14. Changes to these Terms",
      blocks: [
        "We may update these Terms from time to time. Material changes will be posted on the site and/or emailed at least 14 days before taking effect. Continued use after that date constitutes acceptance. The binding version is the one published on the site at any given time. In case of conflict between the Hebrew and English versions, the Hebrew version prevails.",
      ],
    },
    {
      id: "misc",
      heading: "15. Miscellaneous and contact",
      blocks: [
        "If any provision is held invalid, the remaining provisions remain in full force. Failure to enforce a right is not a waiver. You may not assign your rights under these Terms without our written consent.",
        `Questions, requests and cancellations: ${E.email}. Postal address: ${E.address}.`,
      ],
    },
  ],
};

/* ====================================================================================== */
/*                                       PRIVACY                                          */
/* ====================================================================================== */

const privacyHe: LegalDoc = {
  title: "מדיניות פרטיות",
  intro:
    `מדיניות זו מתארת כיצד ${E.legalName} (להלן: "המפעילה", "אנחנו") אוספת, מעבדת, שומרת ומשתפת מידע אישי במסגרת שירות ${E.brandHe}, בהתאם לחוק הגנת הפרטיות, התשמ\"א-1981 ותקנותיו (לרבות תקנות הגנת הפרטיות (אבטחת מידע), התשע\"ז-2017 ותיקון מס' 13 לחוק), ולמשתמשים מהאיחוד האירופי/בריטניה — גם בהתאם ל־GDPR/UK GDPR. ` +
    `מדיניות זו היא גם ה"הודעה" הנדרשת לפי סעיף 11 לחוק הגנת הפרטיות. מסירת המידע אינה חובה על פי דין, אך בלעדיו לא נוכל לספק את השירות.`,
  sections: [
    {
      id: "controller",
      heading: "1. בעל השליטה במידע ופרטי קשר",
      blocks: [
        `בעל השליטה במאגר המידע הוא ${E.legalName}, ${E.regNumber}, ${E.address}. ממונה הגנת הפרטיות: ${E.privacyOfficer}, ${E.email}. ניתן לפנות אלינו בכל שאלה, בקשה או תלונה בנושא פרטיות.`,
      ],
    },
    {
      id: "data",
      heading: "2. איזה מידע אנחנו אוספים",
      blocks: [
        "מידע על ההורה (בעל החשבון):",
        { list: ["שם, כתובת דוא\"ל, מספר טלפון, סיסמה (נשמרת כגיבוב בלבד)", "העדפות שפה, ערכת נושא ונגישות", "הגדרות דיווח והתראות", "רשומות חיוב: מזהה עסקה, מספר קבלה, סכום, מועד, ארבע ספרות אחרונות וסוג הכרטיס (ללא מספר כרטיס מלא), סטטוס חידוש אוטומטי, כתובת דוא\"ל לקבלות שבחרת ותיעוד הסכמתך בעת התשלום", "לוגים טכניים: כתובת IP, סוג דפדפן, זמני גישה, שגיאות"] },
        "מידע על הורים נוספים שהוגדרו לקבלת דיווחים:",
        { list: ["שם תצוגה ומספר טלפון (המאומת באמצעות קוד בוואטסאפ)"] },
        "מידע על הילד:",
        { list: ["שם התצוגה שבחר ההורה, מספר הוואטסאפ של הילד (לאחר הקישור), מזהה סשן טכני, סטטוס חיבור", "תוכן הודעות טקסט, תמונות, סרטונים, הודעות קוליות ומדבקות בצ'אטים של הילד — נכנסות ויוצאות, בהתאם להגדרות ההורה — לצורך ניתוח בטיחות", "מטא־נתונים: מזהי צ'אט, שמות קבוצות, שמות/מספרי אנשי קשר, כיוון ההודעה, זמן", "אירועים: הצטרפות/הסרה מקבוצות, חסימת אנשי קשר, פנייה ממספרים לא מוכרים", "תוצאות ניתוח: ציוני סיכון, קטגוריות, תקצירים, גיבובים (hash) של תוכן לזיהוי חוזר"] },
        "מידע על צדדים שלישיים: אנשי הקשר וחברי הקבוצות של הילד המופיעים בהודעות (שם, מספר, תוכן שכתבו). מידע זה מעובד באופן אגבי ומינימלי לצורך הגנה על הילד בלבד, ואינו משמש לכל מטרה אחרת.",
      ],
    },
    {
      id: "purposes",
      heading: "3. מטרות העיבוד והבסיס המשפטי",
      blocks: [
        { list: [
          "אספקת השירות: ניתוח הודעות, זיהוי סיכונים ושליחת התראות להורים — על בסיס ביצוע ההסכם עמך והסכמת ההורה כאפוטרופוס בשם הקטין;",
          "ניהול חשבון, אימות זהות, אבטחה ומניעת הונאות — על בסיס אינטרס לגיטימי וחובות חוקיות;",
          "חיוב, חשבוניות ורישום עסקאות — על בסיס ביצוע ההסכם וחובות חוקיות (דיני מס וניהול ספרים);",
          "שיפור הדיוק של מנגנוני הזיהוי באמצעות סטטיסטיקה מצרפית ותבניות שאינן מזוהות — על בסיס אינטרס לגיטימי;",
          "עמידה בדרישות דין, צווים שיפוטיים ודרישות רשויות מוסמכות.",
        ] },
        "איננו משתמשים במידע לפרסום ממוקד, איננו מוכרים מידע אישי ואיננו מעבירים אותו לגורמי שיווק.",
      ],
    },
    {
      id: "children",
      heading: "4. מידע על קטינים",
      blocks: [
        "השירות מיועד לשימוש הורים בלבד; ילדים אינם נרשמים אליו ואינם משתמשים בו ישירות. עיבוד המידע של הילד נעשה מכוח סמכותו והסכמתו של ההורה/האפוטרופוס, אשר אחראי ליידע את הילד במידת הצורך. למשתמשים מארה\"ב: לצורך COPPA, ההורה הוא שנותן הסכמה ניתנת לאימות לעיבוד מידע של ילד מתחת לגיל 13. הורה רשאי בכל עת לעיין במידע על ילדו, לדרוש מחיקתו ולהפסיק את העיבוד על ידי מחיקת הילד מהשירות.",
      ],
    },
    {
      id: "ai",
      heading: "5. עיבוד אוטומטי ובינה מלאכותית",
      blocks: [
        "תוכן הודעות ומדיה נשלח לניתוח אוטומטי אצל ספקי מודלים של בינה מלאכותית (לרבות סיווג טקסט ותמונה). העברת התוכן נעשית בערוץ מוצפן, למטרת הסיווג בלבד, ובכפוף להתחייבויות חוזיות של הספקים שלא לעשות שימוש בתוכן למטרותיהם ככל שהדבר אפשרי בתנאיהם. חלק מהניתוח מתבצע בכלים מקומיים ללא העברה לצד שלישי. ההתראות אינן החלטה משפטית או מנהלית בעלת תוקף כלפיך, ואתה רשאי לפנות אלינו לקבלת הסבר על אופן פעולת המנגנון.",
      ],
    },
    {
      id: "sharing",
      heading: "6. שיתוף מידע",
      blocks: [
        "אנו משתפים מידע רק במידה הנדרשת עם:",
        { list: [
          "ספקי תשתית ואחסון ענן, ספקי דוא\"ל ו־SMS/וואטסאפ, ספקי מודלים של בינה מלאכותית וספקי סליקה — כמעבדי מידע מטעמנו, הכפופים להסכמי עיבוד וסודיות;",
          "ההורים שהוגדרו בחשבון — שמקבלים התראות הכוללות, בהתאם להגדרותיך, תקציר או תוכן של הודעות של הילד;",
          "רשויות אכיפה, בתי משפט או רשויות מוסמכות — כאשר הדבר נדרש על פי דין, צו, או להגנה על שלום קטין;",
          "רוכש או נעבר במסגרת מיזוג, רכישה או העברת פעילות — בכפוף להמשך תחולת מדיניות זו.",
        ] },
      ],
    },
    {
      id: "transfer",
      heading: "7. העברת מידע מחוץ לישראל",
      blocks: [
        "חלק מספקינו פועלים מחוץ לישראל (לרבות ארה\"ב והאיחוד האירופי). העברה כזו נעשית בהתאם לתקנות הגנת הפרטיות (העברת מידע אל מאגרי מידע שמחוץ לגבולות המדינה), התשס\"א-2001, ובכפוף לאמצעי הגנה מתאימים כגון סעיפים חוזיים סטנדרטיים, החלטות נאותות או הסכמתך. ישראל מוכרת על ידי האיחוד האירופי כמדינה בעלת רמת הגנה נאותה.",
      ],
    },
    {
      id: "retention",
      heading: "8. תקופות שמירה",
      blocks: [
        { list: [
          "תוכן הודעות שסומן כמסוכן/חשוד: תקציר ותוצאות ניתוח נשמרים עד 90 ימים לצורך הצגת היסטוריה להורה ושיפור הדיוק, אלא אם ההורה מחק אותם קודם;",
          "תוכן הודעות שלא סומן: לא נשמר כטקסט מלא; נשמרים גיבוב (hash), מטא־נתונים וציון סיכון עד 30 ימים;",
          "מדיה (תמונות/סרטונים/קול): מעובדת בזיכרון ואינה נשמרת לאחר הסיווג, למעט העברה של קובץ שסומן להורה במסגרת ההתראה;",
          "פרטי חשבון והורים: כל עוד החשבון פעיל ועד 90 ימים לאחר סגירתו;",
          "רשומות חיוב: 7 שנים בהתאם לדיני המס והנהלת החשבונות;",
          "לוגים טכניים ואבטחה: עד 12 חודשים.",
        ] },
        "בתום התקופות המידע נמחק או מותמם (anonymised) באופן בלתי הפיך.",
      ],
    },
    {
      id: "security",
      heading: "9. אבטחת מידע",
      blocks: [
        "אנו מיישמים אמצעי אבטחה פיזיים, טכנולוגיים וארגוניים בהתאם לתקנות אבטחת מידע, לרבות: הצפנת תעבורה (TLS), גיבוב סיסמאות, אימות דו־שלבי בקוד להורים, הרשאות גישה מינימליות, תיעוד גישה, הפרדת סביבות, וגיבויים. עם זאת, אין אבטחה מוחלטת; במקרה של אירוע אבטחה חמור נודיע לרשות להגנת הפרטיות ולנפגעים כנדרש בדין.",
      ],
    },
    {
      id: "rights",
      heading: "10. הזכויות שלך",
      blocks: [
        "בכפוף לדין, עומדות לך הזכויות הבאות, גם בשם ילדך:",
        { list: [
          "עיון במידע השמור עליך (סעיף 13 לחוק הגנת הפרטיות);",
          "תיקון או מחיקה של מידע שאינו נכון, שלם או מעודכן (סעיף 14);",
          "מחיקת החשבון וכל המידע הקשור אליו, בכפוף לחובות שמירה חוקיות;",
          "ביטול הסכמה בכל עת (בלי לפגוע בחוקיות העיבוד שקדם לביטול);",
          "למשתמשי EU/UK: ניידות מידע, הגבלת עיבוד, התנגדות, וזכות להגיש תלונה לרשות פיקוח;",
          "הגשת תלונה לרשות להגנת הפרטיות בישראל.",
        ] },
        `לבקשות: ${E.email}. נשיב בתוך 30 ימים, ונדרוש אימות זהות סביר לפני מסירת מידע.`,
      ],
    },
    {
      id: "cookies",
      heading: "11. עוגיות (Cookies) ואחסון מקומי",
      blocks: [
        "אנו משתמשים בעוגיות הכרחיות בלבד: עוגיית התחברות (session), ואחסון מקומי בדפדפן להעדפות שפה, ערכת נושא ונגישות. איננו משתמשים בעוגיות פרסום או במעקב חוצה־אתרים. ניתן למחוק עוגיות דרך הגדרות הדפדפן; חסימתן תמנע התחברות.",
      ],
    },
    {
      id: "messages",
      heading: "12. הודעות שירות",
      blocks: [
        "נשלח לך ולהורים שהגדרת הודעות שירות בוואטסאפ ובדוא\"ל: קודי אימות, התראות בטיחות, הודעות חיוב, הודעה על השהיית הגנה, ועדכונים מהותיים. הודעות אלה נדרשות לתפעול השירות ואינן \"דבר פרסומת\" לפי סעיף 30א לחוק התקשורת. לא נשלח דיוור שיווקי ללא הסכמה מפורשת, ותמיד תוכל להסיר את עצמך ממנו.",
      ],
    },
    {
      id: "changes",
      heading: "13. שינויים במדיניות",
      blocks: [
        "נעדכן מדיניות זו מעת לעת. שינוי מהותי יפורסם באתר ו/או יישלח בדוא\"ל לפני כניסתו לתוקף. תאריך העדכון האחרון מופיע בראש המסמך. בסתירה בין הגרסה העברית לאנגלית — העברית גוברת.",
      ],
    },
  ],
};

const privacyEn: LegalDoc = {
  title: "Privacy Policy",
  intro:
    `This policy explains how ${E.legalName} ("we", the "Operator") collects, processes, stores and shares personal data in the ${E.brand} service, in accordance with Israel's Protection of Privacy Law 1981 and its regulations (including the Data Security Regulations 2017 and Amendment 13), and — for EU/UK users — the GDPR/UK GDPR. ` +
    `It also serves as the notice required by section 11 of the Protection of Privacy Law. You are not legally obliged to provide the data, but without it we cannot provide the Service.`,
  sections: [
    {
      id: "controller",
      heading: "1. Data controller and contact",
      blocks: [
        `The controller is ${E.legalName}, ${E.regNumber}, ${E.address}. Privacy officer: ${E.privacyOfficer}, ${E.email}. Contact us with any privacy question, request or complaint.`,
      ],
    },
    {
      id: "data",
      heading: "2. What we collect",
      blocks: [
        "About the parent (account holder):",
        { list: ["name, email, phone number, password (stored as a hash only)", "language, theme and accessibility preferences", "reporting and alert settings", "billing records: order ID, receipt number, amount, date, last four digits and card brand (never the full card number), auto-renew status, the receipt email you chose and your checkout consent record", "technical logs: IP address, browser type, access times, errors"] },
        "About additional parents set up to receive alerts:",
        { list: ["display name and phone number (verified by a WhatsApp code)"] },
        "About the child:",
        { list: ["the display name chosen by the parent, the child's WhatsApp number (after linking), a technical session ID, connection status", "the content of text messages, images, videos, voice notes and stickers in the child's chats — incoming and outgoing, per the parent's settings — for safety analysis", "metadata: chat IDs, group names, contact names/numbers, message direction, timestamps", "events: joining/removal from groups, blocking contacts, contact from unknown numbers", "analysis results: risk scores, categories, excerpts, content hashes for repeat detection"] },
        "About third parties: the child's contacts and group members appearing in messages (name, number, what they wrote). This data is processed incidentally and minimally, solely to protect the child, and for no other purpose.",
      ],
    },
    {
      id: "purposes",
      heading: "3. Purposes and legal bases",
      blocks: [
        { list: [
          "providing the Service: analysing messages, detecting risks and alerting parents — performance of our contract with you and the parent's consent as guardian on the minor's behalf;",
          "account management, identity verification, security and fraud prevention — legitimate interest and legal obligations;",
          "billing, invoicing and transaction records — contract performance and legal obligations (tax and bookkeeping law);",
          "improving detection accuracy through aggregate statistics and non-identifying patterns — legitimate interest;",
          "complying with law, court orders and requests from competent authorities.",
        ] },
        "We do not use the data for targeted advertising, do not sell personal data, and do not share it with marketers.",
      ],
    },
    {
      id: "children",
      heading: "4. Children's data",
      blocks: [
        "The Service is for parents only; children do not register or use it directly. The child's data is processed under the authority and consent of the parent/guardian, who is responsible for informing the child as appropriate. For US users: for COPPA purposes, the parent provides verifiable consent to the processing of data about a child under 13. A parent may at any time review their child's data, request its deletion and stop processing by removing the child from the Service.",
      ],
    },
    {
      id: "ai",
      heading: "5. Automated processing and AI",
      blocks: [
        "Message and media content is sent for automated analysis to AI-model providers (including text and image classification). Transfer is encrypted, for classification only, and subject to the providers' contractual commitments not to use the content for their own purposes where their terms allow. Some analysis runs locally without any third-party transfer. Alerts are not legally or administratively binding decisions about you, and you may ask us to explain how the mechanism works.",
      ],
    },
    {
      id: "sharing",
      heading: "6. Sharing",
      blocks: [
        "We share data only as necessary with:",
        { list: [
          "cloud hosting, email, SMS/WhatsApp, AI-model and payment providers — as processors acting on our behalf under data-processing and confidentiality agreements;",
          "the parents configured in the account — who receive alerts containing, per your settings, an excerpt or the content of the child's messages;",
          "law-enforcement, courts or competent authorities — where required by law, order, or to protect a minor's welfare;",
          "an acquirer or successor in a merger, acquisition or transfer of business — subject to this policy continuing to apply.",
        ] },
      ],
    },
    {
      id: "transfer",
      heading: "7. International transfers",
      blocks: [
        "Some of our providers operate outside Israel (including the US and EU). Such transfers comply with the Protection of Privacy (Transfer of Data to Databases Abroad) Regulations 2001 and are subject to appropriate safeguards such as standard contractual clauses, adequacy decisions or your consent. Israel is recognised by the EU as providing an adequate level of protection.",
      ],
    },
    {
      id: "retention",
      heading: "8. Retention",
      blocks: [
        { list: [
          "content flagged as harmful/suspicious: excerpt and analysis results kept up to 90 days to show history to the parent and improve accuracy, unless deleted earlier by the parent;",
          "unflagged content: not stored as full text; hash, metadata and risk score kept up to 30 days;",
          "media (images/video/audio): processed in memory and not stored after classification, except a flagged file forwarded to the parent within the alert;",
          "account and parent details: while the account is active and up to 90 days after closure;",
          "billing records: 7 years per tax and bookkeeping law;",
          "technical and security logs: up to 12 months.",
        ] },
        "At the end of these periods data is irreversibly deleted or anonymised.",
      ],
    },
    {
      id: "security",
      heading: "9. Security",
      blocks: [
        "We apply physical, technical and organisational measures in line with the Data Security Regulations, including TLS encryption in transit, password hashing, code-based verification for parents, least-privilege access, access logging, environment separation and backups. No security is absolute; in the event of a serious security incident we will notify the Privacy Protection Authority and affected individuals as required by law.",
      ],
    },
    {
      id: "rights",
      heading: "10. Your rights",
      blocks: [
        "Subject to law, you have the following rights, also on behalf of your child:",
        { list: [
          "access to the data held about you (section 13 of the Protection of Privacy Law);",
          "correction or deletion of inaccurate, incomplete or outdated data (section 14);",
          "deletion of the account and all associated data, subject to statutory retention duties;",
          "withdrawal of consent at any time (without affecting prior lawful processing);",
          "for EU/UK users: data portability, restriction, objection, and the right to lodge a complaint with a supervisory authority;",
          "lodging a complaint with the Israeli Privacy Protection Authority.",
        ] },
        `Requests: ${E.email}. We respond within 30 days and will require reasonable identity verification before disclosing data.`,
      ],
    },
    {
      id: "cookies",
      heading: "11. Cookies and local storage",
      blocks: [
        "We use strictly necessary cookies only: a login (session) cookie, plus browser local storage for language, theme and accessibility preferences. We use no advertising cookies or cross-site tracking. You can delete cookies in your browser settings; blocking them will prevent login.",
      ],
    },
    {
      id: "messages",
      heading: "12. Service messages",
      blocks: [
        "We send you and the parents you configure service messages by WhatsApp and email: verification codes, safety alerts, billing notices, protection-paused notices and material updates. These are required to operate the Service and are not \"advertising\" under section 30A of Israel's Communications Law. We send no marketing without explicit consent, and you can always opt out of it.",
      ],
    },
    {
      id: "changes",
      heading: "13. Changes",
      blocks: [
        "We update this policy from time to time. Material changes will be posted on the site and/or emailed before taking effect. The last-updated date appears at the top. In case of conflict between the Hebrew and English versions, the Hebrew version prevails.",
      ],
    },
  ],
};

/* ====================================================================================== */
/*                                  ACCESSIBILITY STATEMENT                               */
/* ====================================================================================== */

const a11yHe: LegalDoc = {
  title: "הצהרת נגישות",
  intro:
    `${E.brandHe} רואה חשיבות עליונה במתן שירות שוויוני ונגיש לכלל הציבור, לרבות אנשים עם מוגבלות, ופועלת ליישום הוראות חוק שוויון זכויות לאנשים עם מוגבלות, התשנ\"ח-1998, ותקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע\"ג-2013 (תקנה 35 — נגישות אתרי אינטרנט).`,
  sections: [
    {
      id: "standard",
      heading: "1. רמת הנגישות",
      blocks: [
        "האתר תוכנן ונבנה בהתאם לדרישות התקן הישראלי ת\"י 5568 \"קווים מנחים לנגישות תכנים באינטרנט\", המבוסס על הנחיות WCAG 2.1 של ארגון W3C, ברמה AA. הבדיקות בוצעו בדפדפנים Chrome, Edge, Firefox ו־Safari בגרסאותיהם העדכניות, במחשב ובטלפון נייד, ובשילוב קורא המסך NVDA.",
      ],
    },
    {
      id: "done",
      heading: "2. התאמות שבוצעו באתר",
      blocks: [
        { list: [
          "תפריט נגישות זמין בכל עמוד (סמל האדם בסרגל העליון, ליד בחירת השפה ומצב התצוגה): הגדלה/הקטנה של הטקסט עד 150%, ניגודיות גבוהה, גווני אפור, הדגשת קישורים, גופן קריא, ריווח טקסט, עצירת אנימציות, סמן עכבר גדול והדגשת מיקוד מקלדת. ההעדפות נשמרות בדפדפן;",
          "ניווט מלא באמצעות מקלדת (Tab / Shift+Tab / Enter / Esc), קישור \"דילוג לתוכן הראשי\" בתחילת כל עמוד, וסימון מיקוד ברור;",
          "מבנה סמנטי: כותרות היררכיות, אזורי ניווט (landmarks), תוויות לשדות טופס, תיאורי ARIA לכפתורים ולחלוניות;",
          "ניגודיות צבעים העומדת ביחס 4.5:1 לפחות לטקסט רגיל, ומצב כהה/בהיר לבחירה;",
          "טקסט חלופי לתמונות משמעותיות, והסתרת תמונות דקורטיביות מקוראי מסך;",
          "תמיכה מלאה בעברית (RTL) ובאנגלית (LTR), והתאמה לתצוגה רספונסיבית ולהגדלה עד 400% ללא אובדן תוכן;",
          "כיבוד הגדרת המערכת \"הפחתת תנועה\" (prefers-reduced-motion);",
          "הודעות שגיאה ברורות בטפסים, ללא הסתמכות על צבע בלבד.",
        ] },
      ],
    },
    {
      id: "limits",
      heading: "3. מגבלות ידועות",
      blocks: [
        "חרף מאמצינו, ייתכן שחלקים מסוימים עדיין אינם נגישים במלואם:",
        { list: [
          "קוד ה־QR המוצג לקישור וואטסאפ הוא תמונה שמפיקה אפליקציית וואטסאפ ואינה ניתנת להקראה; לחלופין מוצע קוד קישור מספרי שניתן להקלדה;",
          "אפליקציית וואטסאפ עצמה, שבה מתבצע הקישור, היא מוצר של צד שלישי ואינה בשליטתנו;",
          "רכיבי צד שלישי (כגון שער תשלום) עשויים שלא לעמוד באותה רמת נגישות.",
        ] },
        "אנו ממשיכים לשפר את הנגישות באופן שוטף. אם נתקלתם בקושי — נשמח לדעת ולתקן.",
      ],
    },
    {
      id: "service",
      heading: "4. נגישות השירות",
      blocks: [
        "השירות ניתן באופן מקוון בלבד ואין לו קבלת קהל פיזית. פניות לשירות ניתן להגיש בדוא\"ל, וניתן לבקש קבלת מידע והתראות באמצעי חלופי המותאם לצורכיכם.",
      ],
    },
    {
      id: "coordinator",
      heading: "5. רכז/ת הנגישות ופרטי קשר",
      blocks: [
        `רכז/ת הנגישות: ${E.a11yCoordinator} · טלפון: ${E.a11yPhone} · דוא\"ל: ${E.email}.`,
        "אם נתקלתם בבעיית נגישות באתר, או שיש לכם הצעה לשיפור, אנא פנו אלינו וציינו: תיאור הבעיה, כתובת העמוד, סוג הדפדפן והטכנולוגיה המסייעת שבה השתמשתם. נשיב בתוך 7 ימי עסקים ונפעל לתיקון בהקדם האפשרי.",
      ],
    },
    {
      id: "date",
      heading: "6. תאריך עדכון ההצהרה",
      blocks: [
        `ההצהרה עודכנה לאחרונה בתאריך ${E.updated}. ההצהרה נבדקת ומעודכנת לפחות אחת לשנה, ובכל שינוי מהותי באתר.`,
      ],
    },
  ],
};

const a11yEn: LegalDoc = {
  title: "Accessibility Statement",
  intro:
    `${E.brand} is committed to providing an equal and accessible service to everyone, including people with disabilities, and acts in accordance with Israel's Equal Rights for Persons with Disabilities Law 1998 and the Equal Rights for Persons with Disabilities (Service Accessibility Adjustments) Regulations 2013 (regulation 35 — website accessibility).`,
  sections: [
    {
      id: "standard",
      heading: "1. Conformance level",
      blocks: [
        "The site was designed and built to Israeli Standard IS 5568 \"Web Content Accessibility Guidelines\", which adopts the W3C WCAG 2.1 guidelines, at level AA. Testing was performed in current versions of Chrome, Edge, Firefox and Safari, on desktop and mobile, and with the NVDA screen reader.",
      ],
    },
    {
      id: "done",
      heading: "2. Accessibility features",
      blocks: [
        { list: [
          "An accessibility menu on every page (the person icon in the top bar, next to language and display mode): text scaling up to 150%, high contrast, grayscale, link highlighting, readable font, text spacing, stop animations, large cursor and keyboard-focus highlighting. Preferences persist in the browser;",
          "Full keyboard navigation (Tab / Shift+Tab / Enter / Esc), a \"skip to main content\" link at the top of every page, and clear focus indication;",
          "Semantic structure: hierarchical headings, landmarks, form labels, ARIA descriptions for buttons and dialogs;",
          "Colour contrast of at least 4.5:1 for body text, with light and dark modes;",
          "Alternative text for meaningful images; decorative images hidden from screen readers;",
          "Full Hebrew (RTL) and English (LTR) support, responsive layout, and zoom up to 400% without loss of content;",
          "Respect for the OS \"reduce motion\" setting (prefers-reduced-motion);",
          "Clear form error messages that do not rely on colour alone.",
        ] },
      ],
    },
    {
      id: "limits",
      heading: "3. Known limitations",
      blocks: [
        "Despite our efforts, some parts may not yet be fully accessible:",
        { list: [
          "The QR code shown for WhatsApp linking is an image generated by WhatsApp and cannot be read aloud; a numeric pairing code that can be typed is offered as an alternative;",
          "The WhatsApp application itself, where linking is completed, is a third-party product outside our control;",
          "Third-party components (such as the payment gateway) may not meet the same level of accessibility.",
        ] },
        "We continuously improve accessibility. If you encounter a barrier, please tell us so we can fix it.",
      ],
    },
    {
      id: "service",
      heading: "4. Service accessibility",
      blocks: [
        "The Service is provided online only and has no physical reception. You may contact us by email and request information or alerts through an alternative channel suited to your needs.",
      ],
    },
    {
      id: "coordinator",
      heading: "5. Accessibility coordinator and contact",
      blocks: [
        `Accessibility coordinator: ${E.a11yCoordinator} · Phone: ${E.a11yPhone} · Email: ${E.email}.`,
        "If you experience an accessibility problem on the site, or have a suggestion, please contact us and include: a description of the problem, the page address, your browser and the assistive technology used. We will respond within 7 business days and work to resolve it as soon as possible.",
      ],
    },
    {
      id: "date",
      heading: "6. Statement date",
      blocks: [
        `This statement was last updated on ${E.updated}. It is reviewed and updated at least once a year and whenever the site changes materially.`,
      ],
    },
  ],
};

export const legalDocs: Record<"terms" | "privacy" | "accessibility", Record<Locale, LegalDoc>> = {
  terms: { he: termsHe, en: termsEn },
  privacy: { he: privacyHe, en: privacyEn },
  accessibility: { he: a11yHe, en: a11yEn },
};
